<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useFixtureStore } from '../stores/fixtureStore'
import { useProjectionsStore } from '../stores/projections'
import MarketDetailsPopup from '../components/MarketDetailsPopup.vue'
import KeyboardShortcutsModal from '../components/KeyboardShortcutsModal.vue'
import { useAuthStore } from '../stores/authStore'
import { useUserStore } from '../stores/userStore'
import UsersMenuPopup from '../components/UsersMenuPopup.vue'
import axios from '../axios'
import FixtureInfo from '../components/FixtureInfo.vue'
import NoProjectionsState from '../components/NoProjectionsState.vue'
import FloatingToolbar from '../components/FloatingToolbar.vue'
import { useCurrentUserPermissions } from '../composables/usePermissions'
import { useMenuStore } from '../stores/menuStore'
import { useNotifications } from '../composables/useNotifications'
import { useBalancedLinesStore } from '../stores/balancedLines'
import { useLeagueTeamsStore } from '../stores/leagueTeamsStore'
import ConfirmationDialog from '../components/ConfirmationDialog.vue'
import PublishSuccessOverlay from '../components/fixtures/PublishSuccessOverlay.vue'
import PublishLoadingOverlay from '../components/fixtures/PublishLoadingOverlay.vue'
import GameCountdownTimer from '../components/fixtures/GameCountdownTimer.vue'
import CsvDownloadButton from '../components/CsvDownloadButton.vue'
import MilestonesCsvDownloadButton from '../components/MilestonesCsvDownloadButton.vue'
import FeatureIdeaBanner from '../components/FeatureIdeaBanner.vue'
import ClickhouseTimelineView from '../components/ClickhouseTimelineView.vue'
import ClickhouseTimelineViewV2 from '../components/ClickhouseTimelineViewV2.vue'
import ClickhouseTimelineViewV3 from '../components/ClickhouseTimelineViewV3.vue'
import BalancedLinesPolling from '../components/fixtures/BalancedLinesPolling.vue'
import LeagueSelectModal from '../components/fixtures/LeagueSelectModal.vue'
// import MonitoringSystemSt  atsHud from '../components/MonitoringSystemStatsHud.vue'


const { hasAnyPermission } = useCurrentUserPermissions()
const route = useRoute()
const router = useRouter()
const fixtureStore = useFixtureStore()
const projectionsStore = useProjectionsStore()
const isLoadingPublishing = ref(false)
const authStore = useAuthStore()
const userStore = useUserStore()


const fixtureId = computed(() => route.params.id)
const fixture = computed(() => fixtureStore.getFixtureById(fixtureId.value))

// Initialize activeTab based on current route or user role default
const initializeActiveTab = () => {
  const currentTab = route.path.split('/').pop()
  
  // If we're on the base fixture route (no tab specified), use role-based default
  if (currentTab === fixtureId.value) {
    const userRole = userStore.currentUser?.role
    return userRole === 'client' ? 'balanced-lines' : 'projections'
  }
  
  return currentTab
}

const activeTab = ref(initializeActiveTab())

// Watch for route changes to update the active tab
watch(
  () => route.path,
  (newPath) => {
    const newTab = newPath.split('/').pop()
    
    // If we're on the base fixture route, use role-based default
    if (newTab === fixtureId.value) {
      const userRole = userStore.currentUser?.role
      activeTab.value = userRole === 'client' ? 'balanced-lines' : 'projections'
    } else {
      activeTab.value = newTab
    }
  }
)

const createProjections = async (payload) => {
  // Implement create projections functionality
  await projectionsStore.createProjectionsBasedOnFiltersAndRoster(payload)
  // fixtureData.value.fixture_projections = projectionsStore.projectionsData
  // try {
  //   await getCsrfCookie()
  //   const response = await axios.post(`/api/projections/create-projections`, {
  //     fixture_id: payload.fixture_id,
  //     leagues: payload.leagues,
  //     seasons: payload.seasons
  //   })

  //   fixtureData.value.fixture_projections = response.data.projections
  //   console.log(response)
  // } catch (error) {
  //   console.error(error)
  // }
}

const showConfirmDialog = ref(false)
const showLeagueModal = ref(false)

const removeProjections = async () => {
  showConfirmDialog.value = true
}

const handleConfirmRemove = async () => {
  await projectionsStore.removeProjections(fixtureId.value)
  // fixtureData.value.fixture_projections = projectionsStore.projectionsData
}

const handleLeagueUpdated = async () => {
  await projectionsStore.fetchProjectionsDataByFixtureId(fixtureId.value)
}



// Clients reach this view at /fixture/:id, admins at /fixture-v1/:id — keep links on the same base.
const fixtureBase = computed(() => (route.path.startsWith('/fixture-v1/') ? '/fixture-v1' : '/fixture'))
const isClientUser = computed(() => userStore.currentUser?.role === 'client')

const changeTab = (tab) => {
  const tabRoute = (typeof tab === 'string' ? tab : tab.name).toLowerCase().replace(' ', '-')
  router.push(`${fixtureBase.value}/${fixtureId.value}/${tabRoute}`)
}
// client mode removed - Each tab with its required permission
const tabs = [
  { name: 'Projections', permission: 'fixtures.projections' },
  { name: 'Balanced Lines', permission: 'fixtures.balanced_lines' },
  { name: 'Milestones', permission: 'fixtures.milestones' },
  { name: 'Settlement', permission: 'fixtures.settlement' },
  { name: 'Specials', permission: 'fixtures.specials' },
  { name: 'Inplay', permission: 'fixtures.inplay' },
  
]




// Computed property to get visible tabs based on permissions
const visibleTabs = computed(() => {
  return tabs.filter(tab => hasAnyPermission.value([tab.permission, 'admin.access']))
})

const showTrueProbability = ref(false)
const showOdds = ref(true)
const showLogs = ref(false)
const showScrapedSettlement = ref(false)

// Sample player data based on your image
const players = [
  {
    id: 1,
    number: 1,
    name: 'Kriss Helmanis',
    position: 'SF',
    seasonAvgMinutes: '28.4',
    projectedMinutes: '27',
    points: { value: '11.70', odds: '13.00' },
    rebounds: { value: '2.97', odds: '3.00' },
    assists: { value: '0.63', odds: '3.30' },
    threePointers: { value: '22.27', odds: '0.70' },
    pra: '0.95',
    turnovers: { value: 'NaN', odds: '1.05' },
    blocks: { value: '4.85', odds: '1.68' },
    steals: { value: '5.39', odds: '' }
  },
  {
    id: 2,
    number: 2,
    name: 'Davids Viksne',
    position: 'SF / SG',
    seasonAvgMinutes: '25.8',
    projectedMinutes: '27',
    points: { value: '10.71', odds: '11.90' },
    rebounds: { value: '1.35', odds: '4.25' },
    assists: { value: '0.35', odds: '1.50' },
    threePointers: { value: '15.88', odds: '0.39' },
    pra: '1.05',
    turnovers: { value: 'NaN', odds: '1.16' },
    blocks: { value: '2.79', odds: '0.91' },
    steals: { value: '3.10', odds: '' }
  }
]

const resetMarkets = () => {
  // Implement reset functionality
}

const createMarkets = () => {
  // Implement create markets functionality
}

// Publication state derives from projections store
const isPublished = computed(() => Boolean(projectionsStore.fixture_published_at))

const formatPublishedAt = (timestamp) => {
  if (!timestamp) return ''
  // Handle possible "YYYY-MM-DD HH:MM:SS" by converting space to 'T'
  const tryParse = (ts) => {
    const d = new Date(ts)
    return isNaN(d.getTime()) ? null : d
  }
  let date = tryParse(timestamp) || tryParse(String(timestamp).replace(' ', 'T'))
  if (!date) return String(timestamp)
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

const publishedAt = computed(() => (projectionsStore.fixture_published_at))

const showPublishSuccess = ref(false)

const publishOdds =   async () => {
  // Prevent duplicate publish when already published
  if (isPublished.value) return

  isLoadingPublishing.value = true
  try {
    const response = await axios.post(`/api/publish-odds/`, {
      fixture_id: fixtureId.value 
    })
    console.log('response', response)
    if (response?.data?.message === 'Fixture published successfully' || response?.data === 'Fixture published successfully') {
      showPublishSuccess.value = true
    }

    if (response?.data?.published_at) {
      projectionsStore.fixture_published_at = response.data.published_at
    } else {
      // Fallback: refresh fixture data to pull latest published_at
      await projectionsStore.fetchProjectionsDataByFixtureId(fixtureId.value)
    }


  } catch (error) {
    console.error('Error publishing odds', error)
  } finally {
    isLoadingPublishing.value = false
  }
  // projectionsStore.publishBalancedLines(fixtureId.value)
  // Additional publish logic here
}

// Add data structure for balanced lines
const selectedMarket = ref(null)
const showMarketDetails = ref(false)

// Sample data for one complete row with all markets
const balancedLines = [
  {
    id: 1,
    player: 'Kriss Helmanis',
    status: 'Suspend',
    minutesRange: '23-31',
    markets: {
      points: {
        id: '2030',
        value: '11.5',
        over: { odds: '1.87', percentage: '49.46%' },
        under: { odds: '1.8', percentage: '50.54%' }
      },
      rebounds: {
        id: '536',
        value: '8.5',
        over: { odds: '2.05', percentage: '45.52%' },
        under: { odds: '1.66', percentage: '54.48%' }
      },
      assists: {
        id: '1635',
        value: '2.5',
        over: { odds: '1.63', percentage: '56.88%' },
        under: { odds: '2.1', percentage: '43.12%' }
      },
      threePointer: {
        id: '1871',
        value: '0.5',
        over: { odds: '1.93', percentage: '47.57%' },
        under: { odds: '1.74', percentage: '52.43%' }
      },
      pra: {
        id: '834',
        value: '22.5',
        over: { odds: '1.77', percentage: '52.04%' },
        under: { odds: '1.9', percentage: '47.96%' }
      },
      turnovers: {
        id: '2755',
        value: '0.5',
        over: { odds: '1.48', percentage: '62.5%' },
        under: { odds: '2.4', percentage: '37.5%' }
      },
      blocks: {
        id: '132',
        value: '4.5',
        over: { odds: '1.2', percentage: '80.3%' },
        under: { odds: '4.0', percentage: '19.7%' }
      },
      steals: {
        id: '0',
        value: '0.5',
        over: { odds: '0', percentage: '0%' },
        under: { odds: '0', percentage: '0%' }
      }
    }
  }
]

const openMarketDetails = (market, type) => {
  selectedMarket.value = { ...market, type }
  showMarketDetails.value = true
}

// Add default shortcuts configuration
const defaultShortcuts = {
  'Publish Odds': '⌘ ⇧ P',
  'Reset Markets': '⌘ ⌥ R',
  'Create Markets': '⌘ ⌥ C',
  'Toggle True Probability': '⌘ ⌥ T',
  'Toggle Odds': '⌘ ⌥ O',
  'Toggle Logs': '⌘ ⌥ L',
  'Toggle Scraped Settlement': '⌘ ⌥ S',
  'Projections Tab': '⌘ ⌥ 1',
  'Balanced Lines Tab': '⌘ ⌥ 2',
  'Milestones Tab': '⌘ ⌥ 3',
  'Settlement Tab': '⌘ ⌥ 4',
  'Specials Tab': '⌘ ⌥ 5',
  'Toggle Shortcuts Menu': '⌘ K',
  'Remove Projections': '⌘ ⌥ D'
}

const shortcuts = ref({ ...defaultShortcuts })
const showShortcutsModal = ref(false)
const showTimelineModal = ref(false)
const showTimelineV2Modal = ref(false)
const showTimelineV3Modal = ref(false)

// Update shortcuts when changed in modal
const updateShortcuts = (newShortcuts) => {
  shortcuts.value = newShortcuts
  // You might want to save these to localStorage or your backend
  localStorage.setItem('keyboard-shortcuts', JSON.stringify(newShortcuts))
}

// Load saved shortcuts on mount
onMounted(() => {
  const savedShortcuts = localStorage.getItem('keyboard-shortcuts')
  if (savedShortcuts) {
    shortcuts.value = JSON.parse(savedShortcuts)
  }
})

// Update handleKeyPress to use dynamic shortcuts
const handleKeyPress = (event) => {
  // Build current shortcut string
  let currentShortcut = []
  if (event.metaKey) currentShortcut.push('⌘')
  if (event.altKey) currentShortcut.push('⌥')
  if (event.shiftKey) currentShortcut.push('⇧')
  if (event.key.length === 1) currentShortcut.push(event.key.toUpperCase())
  else if (event.key !== 'Meta' && event.key !== 'Alt' && event.key !== 'Shift') {
    currentShortcut.push(event.key)
  }
  
  const shortcutString = currentShortcut.join(' ')

  // Check against configured shortcuts
  Object.entries(shortcuts.value).forEach(([action, shortcut]) => {
    if (shortcutString === shortcut) {
      event.preventDefault()
      switch(action) {
        case 'Publish Odds':
          publishOdds()
          break
        case 'Reset Markets':
          resetMarkets()
          break
        case 'Create Markets':
          createMarkets()
          break
        case 'Toggle True Probability':
          showTrueProbability.value = !showTrueProbability.value
          break
        case 'Toggle Odds':
          showOdds.value = !showOdds.value
          break
        case 'Toggle Logs':
          showLogs.value = !showLogs.value
          break
        case 'Toggle Scraped Settlement':
          showScrapedSettlement.value = !showScrapedSettlement.value
          break
        case 'Projections Tab':
          router.push(`${fixtureBase.value}/${fixtureId.value}/projections`)
          break
        case 'Balanced Lines Tab':
          router.push(`${fixtureBase.value}/${fixtureId.value}/balanced-lines`)
          break
        case 'Milestones Tab':
          router.push(`${fixtureBase.value}/${fixtureId.value}/milestones`)
          break
        case 'Settlement Tab':
          router.push(`${fixtureBase.value}/${fixtureId.value}/settlement`)
          break
        case 'Specials Tab':
          router.push(`${fixtureBase.value}/${fixtureId.value}/specials`)
          break
        case 'Toggle Shortcuts Menu':
          showShortcuts.value = !showShortcuts.value
          break
        case 'Remove Projections':
          removeProjections()
          break
      }
    }
  })
}

onMounted(() => {
  window.addEventListener('keydown', handleKeyPress)
})

onUnmounted(() => {
  window.removeEventListener('keydown', handleKeyPress)
})

// Add state for shortcuts visibility
const showShortcuts = ref(false)

// Add this to the script setup section, after other refs
const isCompactView = ref(false)

// Add this near the settings buttons
const isImpersonating = computed(() => authStore.isImpersonating)

const toggleImpersonation = () => {
  if (authStore.isImpersonating) {
    authStore.stopImpersonation()
  } else {
    authStore.startImpersonation('client')
  }
}

// const fixtureData = ref(null)
const loadingFixture = ref(false)
const errorFixture = ref(null)

const fetchFixture = async () => {
  loadingFixture.value = true
  errorFixture.value = null
  
  await projectionsStore.fetchProjectionsDataByFixtureId(fixtureId.value)
  // fixtureData.value = projectionsStore.projectionsData

}

onMounted(() => {

  projectionsStore.fillPlayerLines(fixtureId.value)
})

onMounted(fetchFixture)



watch(fixtureId, fetchFixture)

// Handle fixture selection from timeline modal
const handleFixtureSelected = (newFixtureId) => {
  // Navigate to the new fixture
  router.push(`${fixtureBase.value}/${newFixtureId}`)
  // Close the timeline modal
  showTimelineModal.value = false
}
</script>

<template>
  <!-- <MonitoringSystemStatsHud v-if="userStore.currentUser?.role === 'admin'" /> -->
  <div class="p-6 bg-gray-50 min-h-screen">
    <!-- Add toggle button to the very top right -->
    <!-- <div class="absolute top-4 right-4">
      <button 
        @click="isCompactView = !isCompactView"
        class="p-2 rounded-lg bg-white shadow-sm border border-gray-200 hover:bg-gray-50 transition-colors"
        :title="isCompactView ? 'Expand view' : 'Compact view'"
      >
        <i :class="[
          'fas',
          isCompactView ? 'fa-expand' : 'fa-compress',
          'text-gray-600'
        ]"></i>
      </button>
    </div> -->
 
    <!-- Add Confirmation Dialog -->
    <ConfirmationDialog
      :show="showConfirmDialog"
      title="Remove Projections"
      message="Are you sure you want to remove all projections? This action cannot be undone. Please enter the fixture ID to confirm."
      confirm-text="Remove Projections"
      :required-id="fixtureId"
      @confirm="handleConfirmRemove"
      @close="showConfirmDialog = false"
    />

    <!-- Header -->
    <div class="bg-white rounded-lg shadow-sm p-4 mb-2">

      <!-- length: {{ projectionsStore.balancedLinesProbabilitiesAndOdds.filter(item => item.market_type =='points' && item.player_id == 84).length }} -->
      <!-- {{ projectionsStore.balancedLinesProbabilitiesAndOdds.filter(item => item.market_type =='points' && item.player_id == 84) }} -->
      <FixtureInfo :fixture="projectionsStore.projectionsData" v-if="!loadingFixture && projectionsStore.projectionsData" />
      <!-- <div v-else-if="loadingFixture">Loading...</div> -->
      <div v-else-if="errorFixture">Error loading fixture.</div>
      <template v-if="isCompactView">
        <!-- Compact View - Single row for content -->
        <div class="flex items-center justify-between mb-2">
          <!-- Left side with fixture info -->
          <div class="flex items-center gap-4">
            <h1 class="text-lg font-medium text-gray-800">
              {{ projectionsStore.projectionsData?.home_team?.team_name }} vs {{ projectionsStore.projectionsData?.away_team?.team_name }}
            </h1>
            <button
              v-if="userStore.currentUser?.role === 'admin'"
              type="button"
              class="px-2 py-1 bg-blue-100 text-blue-700 text-sm rounded-full hover:bg-blue-200 transition-colors cursor-pointer"
              @click="showLeagueModal = true"
            >
              {{ projectionsStore.projectionsData?.league?.league_name }}
            </button>
            <span v-else class="px-2 py-1 bg-blue-100 text-blue-700 text-sm rounded-full">
              {{ projectionsStore.projectionsData?.league?.league_name }}
            </span>
          </div>
          
          <!-- Right side with action buttons -->
          <div class="flex items-center gap-2">
            <!-- Toggle buttons -->
            <div class="flex items-center gap-1">
              <button class="p-1.5 rounded hover:bg-gray-100" :class="{ 'text-blue-600': showTrueProbability }">
                <i class="fas fa-percentage"></i>
              </button>
              <button class="p-1.5 rounded hover:bg-gray-100" :class="{ 'text-blue-600': showOdds }">
                <i class="fas fa-calculator"></i>
              </button>
              <button class="p-1.5 rounded hover:bg-gray-100" :class="{ 'text-blue-600': showLogs }">
                <i class="fas fa-list"></i>
              </button>
              <button class="p-1.5 rounded hover:bg-gray-100" :class="{ 'text-blue-600': showScrapedSettlement }">
                <i class="fas fa-sync"></i>
              </button>
            </div>

            <!-- Divider -->
            <div class="h-6 w-px bg-gray-200"></div>

            <!-- Action buttons -->
            <button @click="resetMarkets" 
                    class="p-1.5 text-blue-600 hover:bg-blue-50 rounded">
              <i class="fas fa-redo-alt"></i>
            </button>
            
            <button @click="createMarkets"
                    class="p-1.5 text-blue-600 hover:bg-blue-50 rounded">
              <i class="fas fa-plus"></i>
            </button>

            <!-- Divider -->
            <div class="h-6 w-px bg-gray-200"></div>

            <!-- Publish button -->
            <button 
              @click="publishOdds" :disabled="isPublished"
              :class="{
                'bg-green-600 hover:bg-green-700': isPublished,
                'bg-blue-600 hover:bg-blue-700': !isPublished
              }"
              class="px-3 py-1.5 rounded text-white text-sm flex items-center gap-2"
            >
              <i :class="isPublished ? 'fas fa-check' : 'fas fa-upload'"></i>
              {{ isPublished ? 'Published' : 'Publish' }}
            </button>


            
          </div>
        </div>
      </template>
      <template v-else>
        <!-- Original Full View - keep existing header content //client mode header removed-->
         <!-- <div class="fixed top-0 right-0 w-[300px] bg-white z-[10000] overflow-y-auto h-[200px]">
            <pre>{{ projectionsStore.triggeredIDs.length }} {{ projectionsStore.triggeredIDs }}</pre>
         </div> -->
        <div class="flex justify-between items-start mb-6 relative">
          
          <div>
            <div class="flex items-center gap-2 mb-1">
              <h1 class="text-xl font-semibold text-gray-800">
                Fixture Projections {{ projectionsStore.projectionsData?.date }} #{{ projectionsStore.projectionsData?.id }}
              </h1>
              <router-link
                v-if="userStore.currentUser?.role === 'admin' || userStore.currentUser?.role === 'trader' || userStore.currentUser?.role === 'superadmin'"
                :to="`/fixture/${fixtureId}/projections`"
                class="px-2 py-0.5 bg-amber-100 text-amber-800 text-xs rounded-full font-medium hover:bg-amber-200"
              >
                V2
              </router-link>
              <!-- <span class="px-2 py-1 bg-blue-100 text-blue-700 text-sm rounded-full">
                {{ projectionsStore.projectionsData?.league?.league_name }}
              </span> -->
            </div>
            <div class="flex items-center gap-2 text-gray-500 text-sm">
              <span class="font-semibold text-gray-700">Scheduled Time:</span>
              <span>
                {{
                  projectionsStore.projectionsData?.gamedate
                    
                }}
              </span>
              <GameCountdownTimer v-if="userStore.currentUser?.role === 'admin'" :gamedate="projectionsStore.projectionsData?.gamedate" />
            </div>
            <div class="flex flex-wrap gap-4 mt-2 text-sm text-gray-500">
              <div>
                <span class="font-semibold">Suspended At:</span>
                <span>
                  {{
                    projectionsStore.projectionsData?.auto_suspended_at
                      ? (() => {
                          const date = new Date(projectionsStore.projectionsData.auto_suspended_at);
                          return isNaN(date.getTime())
                            ? projectionsStore.projectionsData.auto_suspended_at
                            : date.toLocaleString('et-EE', {
                                year: 'numeric',
                                month: '2-digit',
                                day: '2-digit',
                                hour: '2-digit',
                                minute: '2-digit'
                              });
                        })()
                      : ''
                  }}
                </span>
              </div>
              <div>
                <span class="font-semibold">Settled At:</span>
                <span>
                  {{
                    projectionsStore.projectionsData?.settlement_at
                      ? (() => {
                          const date = new Date(projectionsStore.projectionsData.settlement_at);
                          return isNaN(date.getTime())
                            ? projectionsStore.projectionsData.settlement_at
                            : date.toLocaleString('et-EE', {
                                year: 'numeric',
                                month: '2-digit',
                                day: '2-digit',
                                hour: '2-digit',
                                minute: '2-digit'
                              });
                        })()
                      : ''
                  }}
                </span>
              </div>
            </div>
          </div>

          <!-- Balanced Lines Polling Component (external monitoring tool; admin/trader only) -->
          <div v-if="!isClientUser" class="flex-1 mx-6">
            <BalancedLinesPolling 
              :fixture-id="fixtureId"
              :bookmaker-id="83"
            />
          </div>

          <div v-if="userStore.currentUser?.role === 'admin'" class="flex items-center gap-4">
            <!-- Action Buttons -->
            <div class="flex items-center gap-2">

              <!-- <button @click="createProjections"
                      class="inline-flex items-center px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all shadow-sm hover:shadow text-sm group">
                <i class="fas fa-calculator mr-1.5 group-hover:rotate-90 transition-transform"></i>
                Create Projections
                <kbd class="ml-1.5 text-xs bg-blue-500/50 px-1.5 py-0.5 rounded">⌘ ⌥ P</kbd>
              </button> -->
              <button @click="removeProjections"
                      class="inline-flex items-center px-3 py-1.5 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-all shadow-sm hover:shadow text-sm group">
                <i class="fas fa-trash-alt mr-1.5 group-hover:rotate-12 transition-transform"></i>
                Remove Projections
                <!-- <kbd class="ml-1.5 text-xs bg-red-500/50 px-1.5 py-0.5 rounded">⌘ ⌥ D</kbd> -->
              </button>
              <!-- <button @click="resetMarkets" 
                      class="inline-flex items-center px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all shadow-sm hover:shadow text-sm group">
                <i class="fas fa-redo-alt mr-1.5 group-hover:-rotate-180 transition-transform duration-500"></i>
                Reset
                <kbd class="ml-1.5 text-xs bg-blue-500/50 px-1.5 py-0.5 rounded">⌘ ⌥ R</kbd>
              </button> -->
              <!-- <button @click="createMarkets"
                      class="inline-flex items-center px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all shadow-sm hover:shadow text-sm group">
                <i class="fas fa-plus mr-1.5 group-hover:rotate-90 transition-transform"></i>
                Create
                <kbd class="ml-1.5 text-xs bg-blue-500/50 px-1.5 py-0.5 rounded">⌘ ⌥ C</kbd>
              </button> -->

              <!-- <button :class="[
                        'px-3 py-1.5 rounded-lg border text-sm transition-all ring-offset-2 hover:shadow group',
                        {
                          'bg-blue-600 text-white ring-2 ring-blue-200': showTrueProbability,
                          'bg-white text-gray-700 hover:bg-gray-50': !showTrueProbability
                        }
                      ]">
                <i class="fas fa-percentage"></i>
                <kbd class="ml-1.5 text-xs px-1.5 py-0.5 rounded"
                     :class="showTrueProbability ? 'bg-blue-500/50' : 'bg-gray-100'">⌘ ⌥ T</kbd>
              </button> -->

              <!-- <button :class="[
                        'px-3 py-1.5 rounded-lg border text-sm transition-all ring-offset-2 hover:shadow group',
                        {
                          'bg-blue-600 text-white ring-2 ring-blue-200': showOdds,
                          'bg-white text-gray-700 hover:bg-gray-50': !showOdds
                        }
                      ]">
                <i class="fas fa-calculator"></i>
                <kbd class="ml-1.5 text-xs px-1.5 py-0.5 rounded"
                     :class="showOdds ? 'bg-blue-500/50' : 'bg-gray-100'">⌘ ⌥ O</kbd>
              </button> -->
<!-- 
              <button :class="[
                        'px-3 py-1.5 rounded-lg border text-sm transition-all ring-offset-2 hover:shadow group',
                        {
                          'bg-blue-600 text-white ring-2 ring-blue-200': showLogs,
                          'bg-white text-gray-700 hover:bg-gray-50': !showLogs
                        }
                      ]">
                <i class="fas fa-list"></i>
                <kbd class="ml-1.5 text-xs px-1.5 py-0.5 rounded"
                     :class="showLogs ? 'bg-blue-500/50' : 'bg-gray-100'">⌘ ⌥ L</kbd>
              </button> -->

              <!-- <button :class="[
                        'px-3 py-1.5 rounded-lg border text-sm transition-all ring-offset-2 hover:shadow group',
                        {
                          'bg-blue-600 text-white ring-2 ring-blue-200': showScrapedSettlement,
                          'bg-white text-gray-700 hover:bg-gray-50': !showScrapedSettlement
                        }
                      ]">
                <i class="fas fa-sync group-hover:rotate-180 transition-transform duration-500"></i>
                <kbd class="ml-1.5 text-xs px-1.5 py-0.5 rounded"
                     :class="showScrapedSettlement ? 'bg-blue-500/50' : 'bg-gray-100'">⌘ ⌥ S</kbd>
              </button> -->
            </div>

            <!-- Divider -->
            <div class="h-6 w-px bg-gray-200"></div>

            <!-- Settings Buttons -->
            <!-- <button @click="showShortcutsModal = true"
                    class="text-gray-500 hover:text-gray-700 p-2 rounded-lg transition-colors">
              <i class="fas fa-keyboard"></i>
            </button> -->

            <!-- <button class="text-gray-500 hover:text-gray-700 p-2 rounded-lg">
              <i class="fas fa-cog"></i>
            </button>
            <button class="text-gray-500 hover:text-gray-700 p-2 rounded-lg">
              <i class="fas fa-ellipsis-v"></i>
            </button> -->

            <!-- Add this near the settings buttons -->
            <div class="flex items-center gap-2">
              <!-- <button 
                @click="toggleImpersonation"
                :class="[
                  'px-3 py-1.5 rounded-lg border text-sm transition-all',
                  isImpersonating ? 'bg-yellow-100 text-yellow-800 border-yellow-300' : 'bg-white text-gray-700'
                ]"
              >
                <i class="fas fa-user-secret mr-2"></i>
                {{ isImpersonating ? 'Exit Client View' : 'View as Client' }}
              </button> -->
            </div>
          </div>
        </div>
      </template>
      
      <!-- Teams -->
      <template v-if="isCompactView">
        <!-- Compact Teams Section -->
        <div class="flex items-center justify-between border-gray-100 py-1 px-4 ">
          <div class="flex items-center gap-2">
            <span class="font-medium">{{ projectionsStore.projectionsData?.home_team?.team_name }}</span>
            <span class="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded">Home</span>
          </div>
          
          <div class="flex items-center gap-3">
            <span class="text-gray-400 font-medium">VS</span>
            <button 
              @click="publishOdds" :disabled="isPublished"
              :class="{
                'bg-green-600 hover:bg-green-700': isPublished,
                'bg-blue-600 hover:bg-blue-700': !isPublished
              }"
              class="px-3 py-1 rounded text-white text-sm flex items-center gap-2"
            >
              <i :class="isPublished ? 'fas fa-check' : 'fas fa-upload'"></i>
              {{ isPublished ? 'Published' : 'Publish' }}
            </button>
          </div>
          
          <div class="flex items-center gap-2">
            <span class="font-medium">{{ projectionsStore.projectionsData?.away_team?.team_name }}</span>
            <span class="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded">Away</span>
          </div>
        </div>
      </template>
      <template v-else>
        <!-- Original Teams Section --> 
        <div class="grid grid-cols-3 gap-6 mb-4">
          <!-- Home Team -->
          <div class="bg-gray-50 p-4 rounded-lg flex flex-col justify-center min-h-[100px] relative">
            <div class="absolute -top-3 left-4">
              <button
                v-if="userStore.currentUser?.role === 'admin'"
                type="button"
                class="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full hover:bg-blue-200 transition-colors cursor-pointer"
                @click="showLeagueModal = true"
              >
                {{ projectionsStore.projectionsData?.league?.league_name }}
              </button>
              <span v-else class="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full">
                {{ projectionsStore.projectionsData?.league?.league_name }}
              </span>
            </div>
            <div class="font-medium text-gray-900 text-lg">{{ projectionsStore.projectionsData?.home_team?.team_name }}</div>
            <div class="text-sm text-gray-500">Home Team</div>
          </div>

          <!-- Publish Odds Section -->
          <div  class="flex flex-col items-center justify-between h-[100px]">
            <!-- Top section with VS -->
            <div class="text-xl  font-bold text-gray-400">VS
            <!-- <span v-if="hasAnyPermission(['fixtures.publishOdds'])" class="text-gray-400"> {{ publishedAt }}</span> -->
            
            </div>
            
            <!-- Middle section with button and status // client mode removed-->
            <div
            v-if="hasAnyPermission(['fixtures.publishOdds'])"
            class="flex  flex-col items-center">
              <button @click="publishOdds" :disabled="isPublished"
                      :class="{
                        'bg-green-600 hover:bg-green-700': isPublished,
                        'bg-blue-600 hover:bg-blue-700': !isPublished,
                        'shadow-md': true,
                        'cursor-not-allowed opacity-90': isPublished
                      }"
                      class="w-full px-6 py-2 rounded-lg text-white flex items-center justify-center gap-2 transition-all duration-200 relative group">
                <div class="absolute -top-1 -right-1" v-if="isPublished">
                  <span class="flex h-3 w-3">
                    <span class="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                    <span class="relative inline-flex rounded-full h-3 w-3 bg-green-500"></span>
                  </span>
                </div>
                <i :class="[
                  isPublished ? 'fas fa-check-circle' : 'fas fa-upload',
                  'transition-transform duration-200',
                  isPublished ? 'group-hover:rotate-0' : 'group-hover:-rotate-12'
                ]"></i>
                <span class="font-medium">{{ isPublished ? 'Published' : 'Publish Odds' }}</span>
                <span class="ml-2 text-xs opacity-50 group-hover:opacity-100 transition-opacity">
                  ⌘ ⇧ P
                </span>
              </button>
              <div class="text-sm mt-1" :class="isPublished ? 'text-green-600' : 'text-gray-500'">
                {{ isPublished ? 'Markets are live' : 'Markets ready to publish' }}
              </div>


              
            </div>

            <!-- Bottom section with timestamp -->
            <div class="text-lg text-gray-400 min-h-[16px]">
              {{ isPublished ? `Published ${publishedAt}` : '' }}
            </div>
          </div>

          <!-- Away Team -->
          <div class="bg-gray-50 p-4 rounded-lg flex flex-col justify-center min-h-[100px] relative">
            <div class="absolute -top-3 left-4">
              <button
                v-if="userStore.currentUser?.role === 'admin'"
                type="button"
                class="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full hover:bg-blue-200 transition-colors cursor-pointer"
                @click="showLeagueModal = true"
              >
                {{ projectionsStore.projectionsData?.league?.league_name }}
              </button>
              <span v-else class="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full">
                {{ projectionsStore.projectionsData?.league?.league_name }}
              </span>
            </div>
            <div class="font-medium text-gray-900 text-lg">{{ projectionsStore.projectionsData?.away_team?.team_name }}</div>
            <div class="text-sm text-gray-500">Away Team</div>
          </div>
        </div>
      </template>

      
      <!-- Add the modal component -->
      <KeyboardShortcutsModal
      :show="showShortcutsModal"
      :shortcuts="shortcuts"
      @close="showShortcutsModal = false"
      @update="updateShortcuts"
      />
      <LeagueSelectModal
        :show="showLeagueModal"
        :fixture-id="fixtureId"
        :current-league-id="projectionsStore.projectionsData?.league?.id"
        :current-league-name="projectionsStore.projectionsData?.league?.league_name"
        @close="showLeagueModal = false"
        @league-updated="handleLeagueUpdated"
      />
    </div>
    <!-- Tabs -->
    <div class="bg-white rounded-lg p-4 shadow-sm ">
      
      <!-- Tabs Navigation -->
      <div class="bg-gray-800 rounded-t-lg">
        <nav class="flex items-center justify-between">
          <!-- Left side - Regular tabs -->
          <div class="flex">
            <router-link 
              v-for="(tab, index) in visibleTabs" 
              :key="tab.name"
              :to="`${fixtureBase}/${fixtureId}/` + tab.name.toLowerCase().replace(' ', '-')"
              custom
              v-slot="{ navigate, isActive }"
            >
              <button 
                @click="navigate"
                :class="[
                  'px-6 py-3 font-medium transition-all relative group',
                  isActive 
                    ? 'text-white bg-blue-600 border-blue-400' 
                    : 'text-gray-400 hover:text-gray-200 hover:bg-gray-700/50'
                ]"
              >
                <div class="flex items-center gap-2">
                  <i :class="[
                    'fas',
                    {
                      'fa-chart-line': tab.name === 'Projections',
                      'fa-balance-scale': tab.name === 'Balanced Lines',
                      'fa-flag': tab.name === 'Milestones',
                      'fa-check-circle': tab.name === 'Settlement',
                      'fa-star': tab.name === 'Specials',
                      'fa-play': tab.name === 'Inplay'
                    }
                  ]"></i>
                  {{ tab.name }} 
                  <span v-if="showShortcuts" 
                        class="ml-1 text-xs opacity-50 group-hover:opacity-100">
                    ⌘ ⌥ {{ index + 1 }}
                  </span>
                </div>
                <div v-if="isActive" 
                     class="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-400"></div>
              </button>
            </router-link>
          </div>
          
          <!-- Right side - Timeline buttons -->
          <div v-if="userStore?.currentUser?.role === 'admin'" class="flex items-center gap-2">
            <button 
              @click="showTimelineModal = true"
              class="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm flex items-center gap-2 transition-all"
            >
              <i class="fas fa-history"></i>
              Timeline
            </button>
            <button 
              @click="showTimelineV2Modal = true"
              class="px-4 py-2 bg-white border border-purple-500 text-purple-600 hover:bg-purple-50 rounded-lg text-sm flex items-center gap-2 transition-all"
            >
              <i class="fas fa-history"></i>
              Timeline v2
            </button>
            <button 
              @click="showTimelineV3Modal = true"
              class="px-4 py-2 bg-white border border-purple-500 text-purple-600 hover:bg-purple-50 rounded-lg text-sm flex items-center gap-2 transition-all"
            >
              <i class="fas fa-history"></i>
              Timeline v3
            </button>
          </div>
        </nav>
      </div>

      <!-- Tab Content -->
      <router-view 
        :key="route.fullPath"
        v-slot="{ Component }">
        <keep-alive>
          <component :is="Component" v-if="activeTab !== 'projections'" />
          <NoProjectionsState 
            v-else
            :fixtureData="projectionsStore.projectionsData"
            @create="createProjections"
          />
        </keep-alive>
      </router-view>
    </div>
    <!-- Floating Toolbar -->
    <!-- <FloatingToolbar /> -->
    
    <!-- CSV Download Button - Only show on Balanced Lines tab -->
    <CsvDownloadButton 
      v-if="activeTab === 'balanced-lines'"
      :fixture-id="fixtureId"
      file-name="balanced-lines"
    />
    
    <!-- Milestones CSV Download Button - Only show on Milestones tab -->
    <MilestonesCsvDownloadButton 
      v-if="activeTab === 'milestones'"
      :fixture-id="fixtureId"
      file-name="milestones"
    />
    <!-- add overlay for isLoadingPublishing -->
    <PublishLoadingOverlay
      :show="isLoadingPublishing"
      message="Publishing fixture..."
    />
    <!-- Publish success overlay -->
    <PublishSuccessOverlay
      :show="showPublishSuccess"
      message="Fixture published successfully"
      @close="showPublishSuccess = false"
    />
    
    <!-- ClickHouse Timeline Modal -->
    <ClickhouseTimelineView
      :show="showTimelineModal"
      :fixtureId="fixtureId"
      @close="showTimelineModal = false"
      @fixture-selected="handleFixtureSelected"
    />
    
    <!-- ClickHouse Timeline V2 Modal -->
    <ClickhouseTimelineViewV2
      :show="showTimelineV2Modal"
      :fixtureId="fixtureId"
      @close="showTimelineV2Modal = false"
    />
    
    <!-- ClickHouse Timeline V3 Modal -->
    <ClickhouseTimelineViewV3
      :show="showTimelineV3Modal"
      :fixtureId="fixtureId"
      @close="showTimelineV3Modal = false"
    />
  </div>
</template>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.balanced-lines-cell {
  @apply px-4 py-3 border-r last:border-r-0;
}

.stat-value {
  @apply font-medium;
}

.stat-odds {
  @apply text-sm text-gray-500;
}

.stat-percentage {
  @apply text-xs text-gray-400;
}

.market-cell {
  @apply px-4 py-3 border-r border-gray-200 cursor-pointer hover:bg-gray-50;
}

.market-value {
  @apply flex items-center gap-1;
}

.market-odds {
  @apply text-sm text-gray-600;
}

.market-percentage {
  @apply text-xs text-gray-400;
}

.market-badge {
  @apply absolute -top-3 -right-3 bg-red-500 text-white text-xs px-1.5 rounded-full;
}

/* Add these to your existing styles */
.market-cell {
  @apply relative;
}

.market-arrow {
  @apply flex items-center justify-center w-4;
}

.market-content {
  @apply flex flex-col;
}

.market-row {
  @apply flex items-center gap-2;
}

.market-value {
  @apply flex items-center gap-1 text-sm font-medium;
}

.market-odds {
  @apply text-sm text-gray-600;
}

.market-percentage {
  @apply text-xs text-gray-400;
}

.market-badge {
  @apply absolute -top-3 -right-3 bg-red-500 text-white text-xs px-1.5 rounded-full;
}

/* Update/add these styles */
.market-cell {
  @apply relative p-0;
}

.market-content {
  @apply bg-white rounded border border-gray-100 p-2 hover:bg-gray-50 transition-colors;
}

.market-badge {
  @apply absolute -top-2 -right-2 bg-red-500 text-white text-xs px-1.5 rounded-full z-10;
}

.market-arrow-container {
  @apply w-6 pt-1;
}

.market-value-container {
  @apply flex items-center gap-1 font-medium;
}

.market-odds {
  @apply text-base text-gray-700;
}

.market-percentage {
  @apply text-sm text-gray-400;
}

.market-section {
  @apply flex items-start;
}

.market-over {
  @apply mb-2;
}

/* Add these styles */
.publish-button {
  @apply px-4 py-2 rounded-lg text-white flex items-center gap-2 transition-colors;
}

.publish-button-published {
  @apply bg-green-600 hover:bg-green-700;
}

.publish-button-unpublished {
  @apply bg-blue-600 hover:bg-blue-700;
}

.publish-status {
  @apply text-xs text-gray-500;
}

@keyframes pulse {
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
  100% {
    transform: scale(1);
  }
}

.publish-button-published {
  animation: pulse 2s infinite;
}

.group:hover .opacity-50 {
  opacity: 1;
}

/* Mac symbols for reference:
⌘ - Command
⌥ - Option/Alt
⇧ - Shift
*/

.shortcuts-tooltip {
  @apply absolute right-0 top-full mt-2 w-80 bg-white rounded-lg shadow-xl 
         border border-gray-200 p-4 z-50;
}

.shortcut-row {
  @apply flex justify-between text-sm py-1;
}

.shortcut-key {
  @apply text-gray-500 font-mono;
}

.shortcut-divider {
  @apply border-t border-gray-100 my-2;
}

.compact-header-enter-active,
.compact-header-leave-active {
  transition: all 0.3s ease-out;
}

.compact-header-enter-from,
.compact-header-leave-to {
  transform: translateY(-20px);
  opacity: 0;
}
</style> 